import React, { useEffect, useState } from "react";
import {
  activeGroupIds,
  cleanSelection,
  isTreeGroup,
  pathOfChoice,
  stepOptions,
  stepTitles,
} from "../../utils/productOptions";
import "./ProductOptions.css";

const money = (n) => `₹${Number(n).toLocaleString("en-IN")}`;
const same = (a, b) => String(a ?? "").trim().toLowerCase() === String(b ?? "").trim().toLowerCase();

const Required = ({ show }) =>
  show ? (
    <>
      {" "}
      <span className="required">*</span>
    </>
  ) : null;

const MissingNote = ({ show, children }) =>
  show ? (
    <p className="po-error" role="alert">
      {children}
    </p>
  ) : null;

// A group whose choices are paths ("Ring › Silver › RD1"): one step at a time, each step shown as
// picture cards when the options have pictures (first and last step) and as a drop-down otherwise.
function StepGroup({ group, selectedId, onPick, getImageUrl, showError }) {
  const [path, setPath] = useState(() => (selectedId ? pathOfChoice(group, selectedId) : []));

  // Follow the selection when it changes from outside (a reset, or the default on load).
  useEffect(() => {
    if (selectedId) setPath(pathOfChoice(group, selectedId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const pick = (level, option) => {
    setPath((current) => [...current.slice(0, level), option.segment]);
    onPick(option.leaf ? [option.leaf.id] : []);
  };

  // The steps to show: every step already reached, and the next one to choose.
  const steps = [];
  for (let level = 0; level < 12; level += 1) {
    const options = stepOptions(group, path.slice(0, level));
    if (!options.length) break;
    const chosen = path[level];
    steps.push({ level, options, chosen });
    const picked = options.find((o) => same(o.segment, chosen));
    if (!picked || picked.leaf) break;
  }

  const titles = stepTitles(group);

  return (
    <>
      {steps.map(({ level, options, chosen }) => {
        const title = titles[level] || group.name;
        const asCards = options.some((o) => o.image) && (level === 0 || options.every((o) => o.leaf));
        const missing = showError && !selectedId && level === steps.length - 1;
        return (
          <div className="premium-step" key={level}>
            <h3>
              {title.toUpperCase()}
              <Required show={group.isRequired} />
            </h3>

            {asCards ? (
              <div className={level === 0 ? "premium-style-options gemstone-style-options" : "premium-design-options gemstone-design-grid"}>
                {options.map((option) => {
                  const active = same(option.segment, chosen);
                  return (
                    <button
                      type="button"
                      key={option.segment}
                      className={`${level === 0 ? "premium-style-card" : "premium-design-card"} ${active ? "active" : ""}`}
                      aria-pressed={active}
                      onClick={() => pick(level, option)}
                    >
                      <div className={level === 0 ? "premium-style-image" : "premium-design-image"}>
                        {option.image && <img src={getImageUrl(option.image)} alt={option.segment} />}
                        {active && <span className="premium-selected-check">✓</span>}
                      </div>
                      <strong>{option.segment}</strong>
                      {option.leaf && option.leaf.priceDelta > 0 && <span>+ {money(option.leaf.priceDelta)}</span>}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="premium-select-wrapper">
                <select
                  aria-label={title}
                  value={chosen || ""}
                  onChange={(e) => {
                    const option = options.find((o) => same(o.segment, e.target.value));
                    if (option) pick(level, option);
                    else {
                      setPath((current) => current.slice(0, level));
                      onPick([]);
                    }
                  }}
                  className="custom-select premium-select"
                >
                  <option value="">Select {title}</option>
                  {options.map((option) => (
                    <option key={option.segment} value={option.segment}>
                      {option.segment}
                      {option.leaf && option.leaf.priceDelta > 0 ? ` (+ ${money(option.leaf.priceDelta)})` : ""}
                    </option>
                  ))}
                </select>
                <span className="premium-select-arrow">⌄</span>
              </div>
            )}

            <MissingNote show={missing}>Please select {title.toLowerCase()}.</MissingNote>
          </div>
        );
      })}
    </>
  );
}

// An ordinary group: a drop-down for a long single-choice list (ring sizes), otherwise a list of
// radio buttons (one choice) or check boxes (several), with an explicit "none" when it is optional.
function ChoiceGroup({ group, selectedIds, onPick, showError }) {
  const multiple = group.selectionType === "multiple";
  const none = `No ${group.name.toLowerCase()}`;
  const missing = showError && group.isRequired && selectedIds.length === 0;

  const heading = (
    <h3>
      {group.name.toUpperCase()}
      <Required show={group.isRequired} />
    </h3>
  );

  if (!multiple && group.choices.length > 4) {
    return (
      <div className="premium-step">
        {heading}
        <div className="premium-select-wrapper">
          <select
            aria-label={group.name}
            value={selectedIds[0] || ""}
            onChange={(e) => onPick(e.target.value ? [e.target.value] : [])}
            className="custom-select premium-select"
          >
            <option value="">{group.isRequired ? `Select ${group.name.toLowerCase()}` : none}</option>
            {group.choices.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
                {c.priceDelta > 0 ? ` (+ ${money(c.priceDelta)})` : ""}
              </option>
            ))}
          </select>
          <span className="premium-select-arrow">⌄</span>
        </div>
        <MissingNote show={missing}>Please select {group.name.toLowerCase()}.</MissingNote>
      </div>
    );
  }

  const toggle = (id) => {
    if (multiple) onPick(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
    else onPick([id]);
  };

  const row = (key, label, price, checked, onChange) => (
    <label key={key} className={`certificate-option ${checked ? "active" : ""}`}>
      <input type={multiple ? "checkbox" : "radio"} name={`po-${group.id}`} checked={checked} onChange={onChange} />
      <span className="certificate-checkmark">{checked && "✓"}</span>
      <div className="certificate-option-content">
        <strong>{label}</strong>
        <small>{price > 0 ? `+ ${money(price)}` : money(0)}</small>
      </div>
    </label>
  );

  return (
    <div className="premium-step">
      {heading}
      <div className="certificate-options">
        {!group.isRequired && row("none", none, 0, selectedIds.length === 0, () => onPick([]))}
        {group.choices.map((c) => row(c.id, c.label, c.priceDelta, selectedIds.includes(c.id), () => toggle(c.id)))}
      </div>
      <MissingNote show={missing}>Please select {group.name.toLowerCase()}.</MissingNote>
    </div>
  );
}

// The option groups of a product. `groups` comes from normalizeGroups(); `selection` is
// { [groupId]: [choiceId] }; groups that do not apply to the current selection are not shown.
export default function ProductOptions({ groups, selection, onChange, getImageUrl = (x) => x, showErrors = false }) {
  const active = activeGroupIds(groups, selection);
  const visible = groups.filter((g) => active.has(g.id));
  if (visible.length === 0) return null;

  const setGroup = (groupId, ids) => onChange(cleanSelection(groups, { ...selection, [groupId]: ids }));

  return (
    <div className="premium-customization product-options">
      {visible.map((group) =>
        isTreeGroup(group) ? (
          <StepGroup
            key={group.id}
            group={group}
            selectedId={(selection[group.id] || [])[0]}
            onPick={(ids) => setGroup(group.id, ids)}
            getImageUrl={getImageUrl}
            showError={showErrors && group.isRequired}
          />
        ) : (
          <ChoiceGroup key={group.id} group={group} selectedIds={selection[group.id] || []} onPick={(ids) => setGroup(group.id, ids)} showError={showErrors} />
        )
      )}
    </div>
  );
}
